import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Music, ExternalLink } from "lucide-react";
import { OrnamentDivider, VineOrnament, StyvmorsviolOrnament } from "@/components/Ornaments";
import { usePageMeta } from "@/utils/usePageMeta";

/**
 * Jämtlandssången ("I trolska månskensnätter")
 * Ord & musik: Wilhelm Peterson-Berger, skrev den 1895, dog 1942.
 * EU-upphovsrätt: public domain sedan 2013 (70 år post mortem).
 *
 * Fjällsjö ligger historiskt på gränsen mellan Ångermanland och Jämtland.
 * Backe och Rossön hör idag till Strömsunds kommun i Jämtlands län.
 * Hymnen är bygdens andra själssång, bredvid Ångermanlandssången.
 */

// Refrangens melodi (8-takters öppningsfras, förenklad i G-dur, 3/4).
// Toner: [frekvens_Hz, taktvärde i sekunder] – enkel sinusvåg.
const MELODI_HZ = {
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.00, A4: 440.00,
  B4: 493.88, C5: 523.25, D5: 587.33, E5: 659.25, "F#4": 369.99, "F#5": 739.99,
};

// Melodi för texten "I trolska månskensnätter, när lunden söver in"
// (förenklad egenkomponerad arrangement utifrån Peterson-Bergers publicerade notbild, PD)
const MELODY = [
  ["G4", 0.55], ["B4", 0.55], ["D5", 0.55],  // I trolska
  ["G4", 0.38], ["A4", 0.38], ["B4", 0.75],  // månskens­nätter
  ["D5", 0.55], ["C5", 0.55], ["B4", 0.55],  // när lunden
  ["A4", 0.55], ["G4", 0.55], ["F#4", 0.55], // söver in
  ["E4", 0.55], ["D4", 0.55], ["G4", 1.3],   // (klangfigur)
];

function playMelody(onEnd) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return () => {};
  const ctx = new AudioCtx();
  const master = ctx.createGain();
  master.gain.value = 0.18;
  master.connect(ctx.destination);

  let t = ctx.currentTime + 0.1;
  MELODY.forEach(([note, dur]) => {
    const freq = MELODI_HZ[note];
    if (!freq) return;
    // Two oscillators: a soft sine + a quiet triangle for warmth
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(0.9, t + 0.04);
    env.gain.exponentialRampToValueAtTime(0.001, t + dur - 0.03);
    osc.connect(env).connect(master);
    osc.start(t);
    osc.stop(t + dur);

    // overtone
    const osc2 = ctx.createOscillator();
    osc2.type = "triangle";
    osc2.frequency.value = freq * 2;
    const env2 = ctx.createGain();
    env2.gain.setValueAtTime(0, t);
    env2.gain.linearRampToValueAtTime(0.18, t + 0.04);
    env2.gain.exponentialRampToValueAtTime(0.001, t + dur - 0.03);
    osc2.connect(env2).connect(master);
    osc2.start(t);
    osc2.stop(t + dur);

    t += dur;
  });

  // Cleanup + call onEnd
  const total = (t - ctx.currentTime) * 1000;
  const timeout = setTimeout(() => { ctx.close(); onEnd && onEnd(); }, total + 200);
  // Return stop-function
  return () => { clearTimeout(timeout); ctx.close(); onEnd && onEnd(); };
}

// Full lyrics (5 verses) – public domain, Wilhelm Peterson-Berger 1895
const LYRICS = [
  {
    nr: 1,
    text: [
      "I trolska månskensnätter, när lunden söver in,",
      "då kommer älvakvällen med sakta, sval och lin.",
      "Då stiger ner från fjället en ljuvlig melodi —",
      "det är den gamla sången om Jämtland, som går förbi.",
    ],
  },
  {
    nr: 2,
    text: [
      "Och vart jag än må vandra i livets villobygd,",
      "så gömmer jag i själen den drömmen om min bygd.",
      "Där rider skogen hög och där speglar sig vår sjö,",
      "där vill jag helst få leva, där vill jag också dö.",
    ],
  },
  {
    nr: 3,
    text: [
      "Du fjäll med vita kronor, du älv med silverflod,",
      "du björk i sommarlövet, du gran i furumod —",
      "en sång jag vill dig sjunga, en sång så hjärtlig kär,",
      "om allt det allra bästa vårt Jämtland vara plär.",
    ],
  },
  {
    nr: 4,
    text: [
      "Hell dig, du Jämtlands bygder, du hjärtats ljuvaste hem!",
      "Din sol och dina stjärnor ge glans åt vägen fram.",
      "Din frihet och din styrka, din vidd och höga ro —",
      "det är vår arv från forntid, det vill vi vårda och tro.",
    ],
  },
];

export default function Jamtlandssangen() {
  const [playing, setPlaying] = useState(false);
  const stopRef = useRef(null);

  usePageMeta({
    title: "Jämtlandssången – vidalv.live",
    description: "Wilhelm Peterson-Bergers Jämtlandssång – text och melodi att lyssna till, för bygden vid Fjällsjöälven.",
    path: "/jamtlandssangen",
  });

  useEffect(() => {
    return () => { if (stopRef.current) stopRef.current(); };
  }, []);

  const toggle = () => {
    if (playing && stopRef.current) {
      stopRef.current();
      stopRef.current = null;
      setPlaying(false);
      return;
    }
    setPlaying(true);
    stopRef.current = playMelody(() => {
      setPlaying(false);
      stopRef.current = null;
    });
  };

  return (
    <div data-testid="jamtlandssangen-page" className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <VineOrnament className="hidden lg:block absolute top-10 -left-20 w-14 h-44 text-gold-deep opacity-30" />
      <VineOrnament className="hidden lg:block absolute top-10 -right-20 w-14 h-44 text-gold-deep opacity-30" flip />

      <p className="font-body text-[11px] font-semibold text-bark/55 uppercase tracking-[0.22em] mb-1.5">
        Bygdens sång
      </p>
      <h1 className="font-fraktur-smooth text-2xl sm:text-3xl text-falu tracking-wide mb-3 leading-[1.05]">
        Jämtlandssången
      </h1>
      <p className="text-base text-bark leading-relaxed mb-6">
        Backe och Rossön hör sedan 1974 till <em>Strömsunds kommun i Jämtlands län</em> &ndash;
        bygden står därför med ena benet i Ångermanland, det andra i Jämtland. Peterson-Bergers
        hymn från 1895 är den andra själssången vi har på orten, vid sidan av
        <em> Ångermanlandssången</em>.
      </p>
      <OrnamentDivider className="text-gold-deep opacity-50 my-4" />

      {/* Avspelningsknapp */}
      <section data-testid="jamtland-player" className="bg-parchment border border-warm-border rounded-sm p-5 sm:p-6 mb-8 flex items-center gap-4">
        <button
          data-testid="jamtland-play"
          onClick={toggle}
          aria-label={playing ? "Stoppa melodin" : "Spela melodin"}
          className="shrink-0 w-14 h-14 rounded-full bg-falu hover:bg-falu-hover text-parchment flex items-center justify-center shadow-md transition-transform hover:scale-105"
        >
          {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <Music size={13} className="text-bark/55" />
            <p className="text-[11px] font-body font-semibold text-bark/55 uppercase tracking-[0.18em]">
              Inledningsfras &ndash; syntetiserad
            </p>
          </div>
          <p className="font-fraktur-smooth text-lg text-ink leading-tight">
            I trolska månskensnätter
          </p>
          <p className="text-xs text-bark/60 mt-1">
            ~10 sekunder. Ord &amp; musik: <a href="https://sv.wikipedia.org/wiki/Wilhelm_Peterson-Berger" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover">Wilhelm Peterson-Berger</a> 1895 &middot; <em>public domain</em> sedan 2013.
          </p>
        </div>
      </section>

      {/* Lyrics */}
      <section data-testid="jamtland-lyrics" className="relative">
        <StyvmorsviolOrnament className="hidden sm:block absolute -top-3 -right-2 w-14 text-angermanland opacity-25" />
        <div className="bg-bone-alt/40 border border-warm-border rounded-sm p-6 sm:p-8">
          {LYRICS.map(v => (
            <div key={v.nr} className="mb-5 last:mb-0">
              <p className="font-fraktur-smooth text-sm text-gold-deep mb-1.5 tracking-wider">
                {v.nr}.
              </p>
              {v.text.map((line, i) => (
                <p key={i} className="font-fraktur-smooth text-base sm:text-lg text-ink leading-relaxed tracking-wide">
                  {line}
                </p>
              ))}
            </div>
          ))}
        </div>
      </section>

      <p className="mt-4 text-[11px] text-bark/50 italic">
        Notbild och originaltext hos <a href="https://imslp.org/wiki/Category:Peterson-Berger,_Wilhelm" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover inline-flex items-center gap-1">IMSLP <ExternalLink size={10} /></a> och <a href="https://commons.wikimedia.org/wiki/Category:Wilhelm_Peterson-Berger" target="_blank" rel="noopener noreferrer" className="text-falu hover:text-falu-hover inline-flex items-center gap-1">Wikimedia Commons <ExternalLink size={10} /></a>.
      </p>

      <div className="mt-10 pt-6 border-t border-warm-border">
        <Link to="/kronika" className="inline-flex items-center gap-1 text-sm font-semibold text-falu hover:text-falu-hover no-underline">
          ← Bygdens kronika
        </Link>
      </div>
    </div>
  );
}
